import { SliderBreakpointManager } from './SliderBreakpointManager';
import { SliderNavigation } from './SliderNavigation';
import { SliderPagination } from './SliderPagination';
import { SliderConfig } from './SliderConfig';

class SliderBase {
	public config: SliderConfig;
	public overflow: boolean;
	public currentItem: number;
	public itemsPerView: number;
	public totalItems: number;
	public container: HTMLElement;
	public wrapper: HTMLElement;
	public items: NodeListOf<HTMLElement>;
	public pagination: SliderPagination;
	private isScrolling: number;
	private navigation: SliderNavigation;

	public constructor(config: SliderConfig) {
		this.config = config;
		this.currentItem = 0;
		this.isScrolling = null;
		this.itemsPerView = Math.abs(this.config.itemsPerView);
		this.container = this.config.target;
		this.wrapper = this.container.querySelector('.scroll-slider-wrapper');
		this.items = this.container.querySelectorAll('.scroll-slider-wrapper > *');
		this.totalItems = this.items.length;

		this.navigation = new SliderNavigation(this);
		this.pagination = new SliderPagination(this);
		this.pagination.initPagination();

		if (this.config.breakpoints) {
			new SliderBreakpointManager(this);
		}

		this.addSliderEventListener();

		window.setTimeout(() => this.handleOverflow(), 200);
	}

	public scrollToCurrentItem(scrollBehavior: ScrollBehavior = 'smooth'): void {
		let offset: number = 0;
		let style: CSSStyleDeclaration;

		for (let i: number = 0; i < this.currentItem; i++) {
			style = getComputedStyle(this.items[i]);
			offset +=
				this.items[i].offsetWidth +
				parseInt(style.marginRight, 10) +
				parseInt(style.marginLeft, 10);
		}

		this.wrapper.scrollTo({ left: offset, top: 0, behavior: scrollBehavior });
		this.pagination.updatePagination();
	}

	public prevItem(): void {
		if (this.config.loop) {
			if (this.itemsPerView === 1) {
				this.currentItem =
					this.currentItem > 0 ? this.currentItem - 1 : this.totalItems - 1;
			} else {
				if (this.currentItem > 0 && this.currentItem < this.itemsPerView) {
					this.currentItem = 0;
				} else {
					this.currentItem -= this.itemsPerView;
				}
				if (this.currentItem < 0) {
					this.currentItem = this.totalItems - this.itemsPerView;
				}
			}
		} else {
			this.currentItem -= this.itemsPerView;
			if (this.currentItem < 0) {
				this.currentItem = 0;
			}
		}

		this.scrollToCurrentItem();
	}

	public nextItem(): void {
		this.currentItem += this.itemsPerView;

		if (this.config.loop) {
			if (this.currentItem > this.totalItems - 1) {
				this.currentItem = 0;
			}
		} else if (this.currentItem > this.totalItems - 1) {
			this.currentItem = this.totalItems - 1;
		}

		this.scrollToCurrentItem();
	}

	// Calculate current item depending on wrapper scroll position
	protected calculateCurrentItem(): number {
		const style: CSSStyleDeclaration = getComputedStyle(this.wrapper);
		const paddingLeft: number = parseInt(style.paddingLeft, 10);

		for (let i: number = 0; i < this.totalItems; i++) {
			if (this.items[i].offsetLeft - paddingLeft >= this.wrapper.scrollLeft) {
				return i;
			}
		}

		return 0;
	}

	protected handleScroll(): void {
		if (!this.config.loop) {
			const prevEnabled: boolean = this.wrapper.scrollLeft > 0;
			const nextEnabled: boolean =
				this.wrapper.scrollLeft < this.wrapper.scrollWidth - this.wrapper.clientWidth;

			this.navigation.updateNavigation(prevEnabled, nextEnabled);
		}

		window.clearTimeout(this.isScrolling);

		this.isScrolling = window.setTimeout(() => {
			this.currentItem = this.calculateCurrentItem();
			this.pagination.updatePagination();
		}, 66);
	}

	protected handleOverflow(): void {
		this.overflow = this.wrapper.scrollWidth > this.wrapper.clientWidth;

		if (this.overflow) {
			this.container.classList.add('scroll-enabled');
		} else {
			this.container.classList.remove('scroll-enabled');
			this.currentItem = 0;
			this.scrollToCurrentItem('auto');
		}

		this.handleScroll();
	}

	private addSliderEventListener(): void {
		this.wrapper.addEventListener('scroll', () => this.handleScroll(), { passive: true });
		window.addEventListener('resize', () => this.handleOverflow(), { passive: true });

		this.items.forEach((item: HTMLElement, index: number) => {
			item.addEventListener('click', () => {
				this.currentItem = index;
				this.scrollToCurrentItem();
			});
		});
	}
}

export { SliderConfig, SliderBase };
